import {
  createContext,
  ComponentType,
  Dispatch,
  FunctionComponent,
  useCallback,
  useContext,
  useReducer,
  useEffect,
} from 'react';

export const createStorageHook = (storage: 'localStorage' | 'sessionStorage') => {
  const defaultValue = [{}, () => undefined];

  const StorageContext = createContext(defaultValue);

  const getItem = (key: string) => {
    if (!process.browser) {
      return;
    }

    const item = window[storage].getItem(key);

    if (!item) {
      return;
    }

    return JSON.parse(item);
  };

  const reducer = <T extends {}>(state: Record<string, T>, action: { key: string; value: T }) => {
    if (process.browser) {
      if (action.value === null || action.value === undefined) {
        window[storage].removeItem(action.key);
      } else {
        window[storage].setItem(action.key, JSON.stringify(action.value));
      }
    }

    return {
      ...state,
      [action.key]: action.value,
    };
  };

  const useStorage = <T extends any>(key: string, initialValue: T) => {
    const [state, dispatch] = useContext(StorageContext) as [Record<string, T>, Dispatch<{ key: string; value: T }>];

    const value = (state[key] || getItem(key) || initialValue) as T;

    useEffect(() => {
      const absentKey = !(key in state);
      if (absentKey) {
        dispatch({ key, value });
      }
    }, [state, dispatch]);

    const setValue = useCallback(
      (v: T | ((v: T) => T)) => {
        try {
          const valueToStore = v instanceof Function ? v(value) : v;
          dispatch({ key, value: valueToStore });
        } catch {}
      },
      [dispatch],
    );

    return [value, setValue] as [T, typeof setValue];
  };

  const withStorage = <T extends {}>(key: string, initialValue: T) => {
    return <P extends {}>(
      Component: ComponentType<P>,
    ): ComponentType<P & { [S in typeof storage]: { value: any; setValue: Function } }> => {
      return (props) => {
        const [value, setValue] = useStorage(key, initialValue);

        const storageProperty = {
          [storage]: { value, setValue },
        };

        return <Component {...props} {...storageProperty} />;
      };
    };
  };

  const Storage: FunctionComponent = ({ children }) => {
    return <StorageContext.Provider value={useReducer(reducer, {})}>{children}</StorageContext.Provider>;
  };

  return {
    Storage,
    StorageContext,
    useStorage,
    withStorage,
  };
};
